<template>
    <div
    class="feature-header-base-wrapper"
    :class="{
        'mobile': getMobileAppState,
        'mobile-status-bar-android': getDeviceInUse === 'android',
        'mobile-status-bar-ios': getDeviceInUse === 'ios',
        'feature-like-header': featureLikeHeader
    }">
        <!-- Ghost div to help get mobile status bar size -->
        <div
        ref="helperForStatusBarHeightRef"
        class="to-help-get-ios-status-bar-height"
        :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }"/>
        <!-- Left Button -->
        <button-tertiary
        v-if="headerLeftButtonText && !(showCurtain && !getMobileAppState)"
        buttonStyle="round"
        buttonSize="small"
        class="position-left"
        :icon="upBellowEntry ? 'close' : 'arrow-left-full'"
        :class="{'hide-text': showCurtain && headerLeftButtonText}"
        :label="headerLeftButtonText"
        @clicked="emitLeftButtonText ? emit('buttonLeftClicked') : emit('backOrCloseClicked')"/>
        <!-- Close/Back Button -->
        <button-tertiary
        v-if="!(headerLeftButtonText && getMobileAppState)"
        buttonStyle="round"
        buttonSize="small"
        :icon="getMobileAppState && !upBellowEntry ? 'arrow-left-full' : 'close'"
        :class="{'position-left': getMobileAppState }"
        @clicked="emit('backOrCloseClicked')"/>
        <!-- Position Fixed top Elements -->
        <div class="curtain" :class="{'showing': showCurtain, 'adjust-height': showCurtain }">
            <div
            v-if="showCurtain"
            class="header-content-wrapper"
            :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }">
                <header-top-content
                insideCurtain
                :invertTitleSubtitle="invertTitleSubtitle"
                :title="title"
                :titleMask="titleMask"
                :negativeTitle="negativeTitle"
                :subTitle="subTitle"
                :subTitleIcon="subTitleIcon"/>
                <div v-if="showImageSlot" class="top-image-area">
                    <slot name="top-image-area-slot"></slot>
                </div>
                <div v-if="showSearchSlot" class="top-search-area">
                    <slot name="top-search-area-slot"></slot>
                </div>
                <tabs-generic v-if="tabs" :tabsList="tabs" @clicked="emit('tabClicked', $event)"/>
            </div>
        </div>
        <!-- Header Web Elements -->
        <div v-if="!getMobileAppState" class="header-content-wrapper">
            <div ref="headerTopContentRef" class="feature-header-base-top-wrapper">
                <header-top-content
                :featureLikeHeader="featureLikeHeader"
                :invertTitleSubtitle="invertTitleSubtitle"
                :colorBackground="withHeaderColor"
                :title="title"
                :titleMask="titleMask"
                :negativeTitle="negativeTitle"
                :subTitle="subTitle"
                :subTitleIcon="subTitleIcon"
                :icon="icon"
                :buttonsTopData="buttonsTopData"
                :helperTextTitle="helperTextTitle"/>
            </div>
            <div class="bottom-elements" :class="{'apply-padding-top': showSearchSlot || tabs, 'image-slot-styles': showImageSlot}">
                <div v-if="showImageSlot" class="top-image-area">
                    <slot name="top-image-area-slot"></slot>
                </div>
                <div v-if="showSearchSlot" class="top-search-area">
                    <slot name="top-search-area-slot"></slot>
                </div>
                <tabs-generic v-if="tabs" :tabsList="tabs" @clicked="emit('tabClicked', $event)"/>
            </div>
        </div>
        <!-- Header Mobile Elements -->
        <div v-else class="header-content-wrapper">
            <slot name="top-mobile-header-slot"></slot>
            <div v-if="!hideHeaderTopWrapper" ref="headerTopContentRef" class="feature-header-base-top-wrapper">
                <header-top-content
                :featureLikeHeader="featureLikeHeader"
                :helperTextTitle="helperTextTitle"
                :invertTitleSubtitle="invertTitleSubtitle"
                :colorBackground="withHeaderColor"
                :title="title"
                :titleMask="titleMask"
                :negativeTitle="negativeTitle"
                :subTitle="subTitle"
                :subTitleIcon="subTitleIcon"
                :icon="icon"
                :buttonsTopData="buttonsTopData"/>
            </div>
            <div class="bottom-elements" :class="{'image-slot-styles-mobile': showImageSlot && getMobileAppState}">
                <div v-if="showImageSlot" class="top-image-area">
                    <slot name="top-image-area-slot"></slot>
                </div>
                <div v-if="showSearchSlot" class="top-search-area">
                    <slot name="top-search-area-slot"></slot>
                </div>
                <div v-if="tabs" class="bottom-mobile-like-content-wrapper">
                    <tabs-generic v-if="tabs" :tabsList="tabs" @clicked="emit('tabClicked', $event)"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import HeaderTopContent from '@/components/generic/modals/side/header/TopContent'
import TabsGeneric from '@/components/generic/tabs/Generic'
/* Vue */
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'

const projectBaseStore = useProjectBaseStore()
const { getMobileAppState, getDeviceInUse, getStatusBarStyle, getAppThemeMode } = storeToRefs(projectBaseStore)
const { updateStatusBarStyle } = projectBaseStore

/* Emmitters */
const emit = defineEmits(['buttonLeftClicked', 'backOrCloseClicked', 'tabClicked'])

/* Props
@upBellowEntry {Boolean} - prop to apply class to animate modal from down to top (instead os right-left default animation)
@invertTitleSubtitle {Boolean} - prop to invert order of Title and Subtitle
@featureLikeHeader {Boolean} - apply styles like a feature wrapper header
@withHeaderColor {Boolean} - prop to fix components in header color
@title {String} - text to display in our title
@titleMask {String} - add mask on our title
@negativeTitle {Boolean} - prop to apply negative color to title
@subTitle {String} - text to display under our title
@subTitleIcon {String} - text to display under our title
@icon: {String} - iconName+path(if needed) to display
@tabs {Array} - tabs to display on header
@showSearchSlot {Boolean} - prop to apply slot for search
@showImageSlot {Boolean} - prop to apply slot for search
@buttonsTopData {Object} - object with info needed for ButtonsOperationsGroup
@emitLeftButtonText {Boolean} - prop to tell which emit we want to do when closing left button on top
@headerLeftButtonText {String} - text to display in left button
@valueScrolled {Number} - value scrolled in detail body
@helperTextTitle {Boolean} - prop to apply helper text title styles
@hideHeaderTopWrapper {Boolean} - prop to hide feature-header-base-top-wrapper
*/
const props = defineProps({
    upBellowEntry: Boolean,
    invertTitleSubtitle: Boolean,
    featureLikeHeader: Boolean,
    withHeaderColor: Boolean,
    title: String,
    titleMask: String,
    negativeTitle: Boolean,
    subTitle: String,
    subTitleIcon: String,
    icon: String,
    buttonsTopData: Object,
    emitLeftButtonText: Boolean,
    headerLeftButtonText: String,
    tabs: Array,
    showSearchSlot: Boolean,
    showImageSlot: Boolean,
    valueScrolled: String,
    helperTextTitle: Boolean,
    hideHeaderTopWrapper: Boolean
})

const headerTopContentRef = ref('')
const helperForStatusBarHeightRef = ref('')
/*
* Computed to validate when we will be showing a "curtain" above content to displayed fixed elements on header
*/
const showCurtain = computed(() => {
    if (props.valueScrolled === 0) return

    let heightToShowCurtain = headerTopContentRef.value.scrollHeight

    if (getMobileAppState.value) {
        const statusBarHeight = helperForStatusBarHeightRef.value.offsetHeight
        heightToShowCurtain -= statusBarHeight
    }

    return props.valueScrolled > heightToShowCurtain
})

watch(() => props.valueScrolled, (newVal, oldVal) => {
    if (getMobileAppState.value && getDeviceInUse.value.toLowerCase() !== 'desktop' && props.valueScrolled > 0 && getAppThemeMode.value !== 'dark') {
        showCurtain.value ? updateStatusBarStyle('default') : updateStatusBarStyle('lightContent')
    }
})

watch(() => props.withHeaderColor, (newVal, oldVal) => {
    validateIfStatusBarChanges()
})

const previousStatusBarStyle = ref(getStatusBarStyle.value)

const validateIfStatusBarChanges = () => {
    if (getAppThemeMode.value !== 'dark') {
        props.withHeaderColor ? updateStatusBarStyle('lightContent') : updateStatusBarStyle('default')
    }
}

onMounted(() => {
    validateIfStatusBarChanges()
})

onBeforeUnmount(() => {
    updateStatusBarStyle(previousStatusBarStyle.value)
})
</script>

<style lang="scss" scoped>
.feature-header-base-wrapper {
    width: 100%;
    height: auto;

    flex-shrink: 0;
    z-index: 2;

    background-color: var(--secondary-light-3);

    &:after {
        content: '';
        width: 100%;
        height: 4px;
        margin-top: -2px;

        position: absolute;

        background-color: var(--secondary-light-4);
    }

    .to-help-get-ios-status-bar-height {
        height: 0;
        position: fixed;

        &.mobile-status-bar-android {
            height: var(--android-top-padding);
        }

        &.mobile-status-bar-ios {
            height: constant(safe-area-inset-top);
            height: env(safe-area-inset-top);
        }
    }

    .tertiary-button-wrapper {
        position: fixed;
        z-index: 3;

        &.position-left {
            top: 24px;
            left: 24px;
        }

        &:not(.position-left) {
            top: 24px;
            right: 24px;
        }

        &.hide-text {
            width: 40px;
            overflow: hidden;
            transition: all .1s linear;

            :deep(.button-tertiary) {
                .label {
                    display: none;
                }
            }
        }
    }

    .curtain {
        width: 100%;
        height: 0;

        position: fixed;
        z-index: 2;

        top: 0;
        left: 0;

        background-color: var(--secondary-light-4);
        transition: all .1s linear;

        overflow: hidden;

        &.showing {
            box-shadow: var(--box-shadow-1-inverted);
        }

        &.adjust-height {
            height: auto;
        }

        .header-content-wrapper {
            padding-top: 24px;

            .generic-tabs-wrapper {
                width: calc(100% - 24px);
                margin-left: 24px;
            }

            .top-search-area {
                padding: 0 24px;
                margin-bottom: 24px;
            }
        }
    }

    .header-content-wrapper {
        width: 100%;
        height: auto;
        padding: 80px 0 0 0;

        position: relative;

        .feature-header-base-top-wrapper {
            width: 100%;
            height: auto;
            margin-bottom: 24px;
            padding: 0 24px 0 24px;

            display: flex;
            justify-content: space-between;
        }

        .bottom-elements {
            width: 100%;
            height: auto;
            padding: 0 0 24px 24px;

            background-color: var(--secondary-light-4);
            border-radius: 24px 24px 0 0;

            &.apply-padding-top {
                padding-top: 16px;
            }

            &.image-slot-styles {
                background-color: unset;
                border-radius: unset;
            }

            &.image-slot-styles-mobile {
                background-color: unset;
                border-radius: unset;
                padding: 0 16px 24px 16px;
            }
        }
    }

    &:not(.mobile) {
        .top-search-area {
            padding-right: 24px;
        }
    }

    &.mobile {
        margin-bottom: -1px;

        .position-left {
            left: 16px;
        }

        &.mobile-status-bar-android {
            padding-top: var(--android-top-padding);

            .tertiary-button-wrapper {
                &.position-left {
                    top: calc(16px + var(--android-top-padding));
                }

                &:not(.position-left) {
                    top: calc(24px + var(--android-top-padding));
                }
            }
        }

        &.mobile-status-bar-ios {
            padding-top: constant(safe-area-inset-top);
            padding-top: env(safe-area-inset-top);

            .tertiary-button-wrapper {
                &.position-left {
                    top: calc(16px + constant(safe-area-inset-top));
                    top: calc(16px + env(safe-area-inset-top));
                }

                &:not(.position-left) {
                    top: calc(24px + constant(safe-area-inset-top));
                    top: calc(24px + env(safe-area-inset-top));
                }
            }
        }

        .curtain {
            .header-content-wrapper {
                padding-top: 16px;

                &.mobile-status-bar-android {
                    padding-top: calc(16px + var(--android-top-padding));
                }

                &.mobile-status-bar-ios {
                    padding-top: calc(16px + constant(safe-area-inset-top));
                    padding-top: calc(16px + env(safe-area-inset-top));
                }

                .generic-tabs-wrapper.mobile {
                    margin-left: 0;
                }
            }
        }

        .header-content-wrapper {
            padding: 72px 0 0 0;

            .feature-header-base-top-wrapper {
                padding: 0 16px;
                margin-bottom: 24px;
                align-items: center;
            }

            .top-search-area {
                padding: 16px 16px 0 16px;
            }

            .bottom-mobile-like-content-wrapper {
                width: 100%;
                height: auto;
                padding: 16px 16px 0 0;

                background-color: var(--secondary-light-4);
                border-radius: 16px 16px 0 0;
            }
        }

        .bottom-elements {
            width: 100%;
            height: auto;
            padding: 0 0 16px 0;

            background-color: var(--secondary-light-4);
            border-radius: 16px 16px 0 0;
        }
    }

    &.feature-like-header {
        background-image: var(--gradient-horizontal);

        .header-content-wrapper {
            padding: 24px 0 0 0;

            &.mobile-status-bar-android {
                padding-top: calc(24px + var(--android-top-padding));
            }

            &.mobile-status-bar-ios {
                padding-top: calc(24px + constant(safe-area-inset-top));
                padding-top: calc(24px + env(safe-area-inset-top));
            }
        }
    }
}
</style>
